import React from 'react';
import { TextBlock as TextBlockType } from './types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

const fontFamilies = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: 'Times New Roman, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
];

const dynamicFields = [
  { label: 'Your Full Name', value: '{{user_full_name}}' },
  { label: 'Your First Name', value: '{{user_first_name}}' },
  { label: 'Your Last Name', value: '{{user_last_name}}' },
  { label: 'Your Role/Position', value: '{{company_position}}' },
  { label: 'Your Email', value: '{{user_email}}' },
  { label: 'Your Phone', value: '{{user_phone}}' },
  { label: 'Company Name', value: '{{company_name}}' },
  { label: 'Company Email', value: '{{company_email}}' },
  { label: 'Company Phone', value: '{{company_phone}}' },
  { label: 'Company Website', value: '{{company_website}}' },
];

interface TextBlockProps {
  block: TextBlockType;
  onChange: (block: TextBlockType) => void;
  isEditing?: boolean;
}

export const TextBlockDisplay: React.FC<{ block: TextBlockType; processedContent?: string }> = ({ 
  block, 
  processedContent 
}) => {
  const content = processedContent || block.content;
  if (!content) {
    return (
      <p className="text-muted-foreground/50 italic" style={{ fontSize: block.fontSize }}>
        Enter text...
      </p>
    );
  }

  return (
    <p
      style={{
        fontSize: block.fontSize,
        fontWeight: block.fontWeight,
        color: block.color,
        fontFamily: block.fontFamily,
        textAlign: block.textAlign || 'left',
        margin: 0,
        lineHeight: 1.4,
      }}
    >
      {content}
    </p>
  );
};

export const TextBlockEditor: React.FC<TextBlockProps> = ({ block, onChange }) => {
  const handleInsertField = (fieldValue: string) => {
    onChange({ ...block, content: block.content + fieldValue });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Content</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Plus className="h-3 w-3 mr-1" />
                Add Field
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground mb-2">Insert dynamic field</p>
                {dynamicFields.map((field) => (
                  <button
                    key={field.value}
                    onClick={() => handleInsertField(field.value)}
                    className="w-full text-left text-xs p-2 rounded hover:bg-muted transition-colors"
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <Input
          value={block.content}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          placeholder="Enter text or use dynamic fields..."
        />
        <p className="text-xs text-muted-foreground">
          Use dynamic fields like {"{{user_full_name}}"} to auto-populate
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Font Size (px)</Label>
          <Input
            type="number"
            value={block.fontSize}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') onChange({ ...block, fontSize: '' as any });
              else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, fontSize: num }); }
            }}
            onBlur={(e) => {
              const num = parseInt(e.target.value);
              if (isNaN(num) || num < 1) onChange({ ...block, fontSize: 14 });
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Font Weight</Label>
          <Select
            value={block.fontWeight}
            onValueChange={(value: 'normal' | 'bold') => onChange({ ...block, fontWeight: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Font Family</Label>
        <Select
          value={block.fontFamily}
          onValueChange={(value) => onChange({ ...block, fontFamily: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fontFamilies.map((font) => (
              <SelectItem key={font.value} value={font.value}>
                {font.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Text Color</Label>
          <div className="flex gap-2">
            <input
              type="color"
              value={block.color}
              onChange={(e) => onChange({ ...block, color: e.target.value })}
              className="h-9 w-12 rounded border cursor-pointer"
            />
            <Input
              value={block.color}
              onChange={(e) => onChange({ ...block, color: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Text Align</Label>
          <Select
            value={block.textAlign || 'left'}
            onValueChange={(value: 'left' | 'center' | 'right') => onChange({ ...block, textAlign: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
